import { Box, Group, Stack } from "@mantine/core";
import { IconDots } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { getSpacing, spacing } from "#app/theme/v2/spacing";
import { Divider } from "#components/atoms/Divider/Divider";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { TextSkeleton } from "#components/atoms/Skeleton/Skeleton";
import { Text } from "#components/atoms/Typographies/Text";
import { DropdownMenu } from "#components/molecules/DropdownMenu/DropdownMenu";
import { NAV_ITEM_ICON_SIZE, NavItem } from "#components/templates/Navbar/NavItem";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";

type NavItemConfig = {
  icon: React.ReactNode;
  label: string;
  to: string;
  disabled?: boolean;
};

type NavbarProps = {
  topItems: NavItemConfig[];
  bottomItems: NavItemConfig[];
  userName?: string;
  companyName?: string;
  onLogout?: () => void;
  isLoadingProfile?: boolean;
};

const Navbar = ({
  topItems,
  bottomItems,
  userName,
  companyName,
  onLogout,
  isLoadingProfile = false,
}: NavbarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      style={
        {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: `${spacing[3]} ${spacing[1]}`,
          gap: spacing[3],
          "--nav-hover-bg": colors.bg.elevation.tertiary,
        } as React.CSSProperties
      }
    >
      <Group style={{ padding: `${spacing[1.5]} ${spacing[4]}` }} gap={spacing[2.5]} wrap="nowrap">
        <Icon name="fabera" size={NAV_ITEM_ICON_SIZE} />
        <Text size="base" type="bold" truncate>
          Fabera
        </Text>
      </Group>

      <Stack gap={spacing[0.5]}>
        {topItems.map((item) => (
          <NavItem
            key={item.to}
            icon={item.icon}
            label={item.label}
            to={item.to}
            disabled={item.disabled}
          />
        ))}
      </Stack>

      <Stack gap={spacing[0.5]} style={{ marginTop: "auto" }}>
        {bottomItems.map((item) => (
          <NavItem
            key={item.to}
            icon={item.icon}
            label={item.label}
            to={item.to}
            disabled={item.disabled}
          />
        ))}

        <Divider style={{ marginTop: spacing[3], marginBottom: spacing[2.5] }} />

        <Group justify="space-between" align="center" wrap="nowrap" gap={spacing[2]}>
          <Flex
            style={{ padding: `${spacing[2]}` }}
            gap={spacing[2.5]}
            align="center"
            justify="space-between"
            wrap="nowrap"
            w="100%"
          >
            <Stack gap={spacing[0.5]}>
              {isLoadingProfile ? (
                <>
                  <TextSkeleton size="small" width={150} />
                  <TextSkeleton size="tiny" width={100} />
                </>
              ) : (
                <>
                  <Text size="small" type="medium" truncate>
                    {userName}
                  </Text>
                  <Text size="tiny" type="regular" truncate>
                    {companyName}
                  </Text>
                </>
              )}
            </Stack>
            <DropdownMenu position="top-end" offset={4}>
              <DropdownMenu.Target>
                <IconButton variant="transparent" aria-label={t("navigation.userMenu")}>
                  <IconDots size={16} />
                </IconButton>
              </DropdownMenu.Target>
              <DropdownMenu.Dropdown style={{ minWidth: getSpacing(55) }}>
                <DropdownMenu.Item variant="destructive" onClick={onLogout}>
                  {t("header.logout")}
                </DropdownMenu.Item>
              </DropdownMenu.Dropdown>
            </DropdownMenu>
          </Flex>
        </Group>
      </Stack>
    </Box>
  );
};

export { Navbar };
